import { useState } from 'react';
import { 
    Plus, 
    X,
    Calendar,
    Clock,
    ClipboardCheck,
    ChevronDown
} from 'lucide-react';
import { Button } from '@mui/material';

type AddConditioningTestSessionProps = {
    sessionHolder: any;
    conditioningTests: any[];
    handleSubmit: (session: any) => void;
    handleCancel: () => void;
};

const AddConditioningTestSession: React.FC<AddConditioningTestSessionProps> = ({ 
    sessionHolder, 
    conditioningTests, 
    handleSubmit, 
    handleCancel 
}) => {
    const [session, setSession] = useState(sessionHolder);
    
    // Round down to nearest 5 minutes for start time
    const roundDownToFiveMinutes = (date: Date) => {
        const minutes = date.getMinutes();
        const roundedMinutes = Math.floor(minutes / 5) * 5;
        const newDate = new Date(date);
        newDate.setMinutes(roundedMinutes, 0, 0);
        return newDate;
    };

    const [startTime, setStartTime] = useState(() => {
        const now = roundDownToFiveMinutes(new Date());
        return now.toTimeString().slice(0, 5); // HH:MM format
    });

    const [showTestDropdown, setShowTestDropdown] = useState(false);

    // const handleFieldChange = (fieldName: string, value: string) => {
    //     setSession({ ...session, [fieldName]: value });
    // };

    const handleDateChange = (field: string, value: string) => {
        setSession({
            ...session,
            [field]: new Date(value).toISOString()
        });
    };

    const handleTestSelect = (testId: number) => {
        setSession({ ...session, test_id: testId });
        setShowTestDropdown(false);
    };

    const selectedTest = conditioningTests.find(test => test.test_id === session.test_id);

    const preSubmit = () => {
        const [hours, minutes] = startTime.split(':').map(Number);
        const testDate = new Date(session.test_date || session.start_time);
        testDate.setHours(hours, minutes, 0, 0);
        
        const newSession = {
            ...session,
            test_date: testDate.toISOString()
        };

        handleSubmit(newSession);
    };

    return (
        <div className="fixed inset-0 bg-black-500 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-6 text-black relative">
                    <div className="absolute inset-0 bg-white opacity-10"></div>
                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center space-x-3 text-white">
                            <Plus className="w-6 h-6" />
                            <h2 className="text-xl font-bold">New Test Session</h2>
                        </div>
                        <button
                            onClick={handleCancel}
                            className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Test Selection */}
                    <div className="space-y-2">
                        <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                            <ClipboardCheck className="w-4 h-4" />
                            <span>Select Test</span>
                        </label>
                        <div className="relative">
                            <button
                                onClick={() => setShowTestDropdown(!showTestDropdown)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none bg-white text-left flex items-center justify-between"
                            >
                                <span className={selectedTest ? 'text-gray-900' : 'text-gray-500'} style={{ fontSize: '1rem' }}>
                                    {selectedTest ? selectedTest.title : 'Select a test...'}
                                </span>
                                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showTestDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            
                            {showTestDropdown && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {conditioningTests.map((test) => (
                                        <button
                                            key={test.test_id}
                                            onClick={() => handleTestSelect(test.test_id)}
                                            className="w-full px-4 py-3 text-left hover:bg-indigo-50 focus:bg-indigo-50 outline-none border-b border-gray-100 last:border-b-0"
                                        >
                                            <div className="flex items-center space-x-2">
                                                <ClipboardCheck className="w-4 h-4 text-indigo-500" />
                                                <span style={{ fontSize: '1rem' }}>{test.title}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Test Date Input */}
                    <div className="space-y-2">
                        <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                            <Calendar className="w-4 h-4" />
                            <span>Test Date</span>
                        </label>
                        <input
                            type="date"
                            value={session.test_date ? session.test_date.split('T')[0] : ''}
                            onChange={(e) => handleDateChange('test_date', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                            style={{ fontSize: '1rem' }}
                        />
                    </div>

                    {/* Start Time Input */}
                    <div className="space-y-2">
                        <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                            <Clock className="w-4 h-4" />
                            <span>Start Time</span>
                        </label>
                        <input
                            type="time"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            step="300" // 5 minute intervals
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                            style={{ fontSize: '1rem' }}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className='bottom-buttons'>
                    <Button
                        style={{margin: "10px 5px 5px", width: '100px'}}
                        disabled={session.title === null || session.title?.length === 0}
                        onClick={preSubmit}
                        variant="contained"
                    >
                        Save
                    </Button>

                    <Button
                        style={{margin: "10px 5px 5px", width: '100px'}}
                        onClick={handleCancel}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                </div> 
            </div>
        </div>
    );
};

export default AddConditioningTestSession;